import React, { useState, useEffect, useContext, useCallback } from 'react'
import { AuthContext } from '../../Api/firebase'
import { QuestionContext } from '../../Context'
import { QuestionBox } from './styles'
import type { Question } from '../../Api'
import { isQuestionActive } from '../../Utils'
import { AnswerRowCon } from './components/AnswerRows/AnswerRowCon'
import { ErrorCon } from './components/Errors/ErrorText'
import { AppExclusive } from './components/AppCallouts/AppExclusive'
import { AppExclusiveOverlay } from './components/AppCallouts/styles'
import { LoginQuestionCallout } from './components/AppCallouts/LoginCallout'
import { Share } from './components/ActionItems/Share'
import { QuestionStatus } from './components/Status/QuestionStatus'
import { LockIcon, PollIcon } from '../LazyComponents/lazyIcons'

type QuestionProps = {
  question: Question
  uiTheme?: 'dark' | 'light'
}

export const QuestionElem: React.FC<QuestionProps> = ({
  question,
  uiTheme = 'light',
}) => {
  const { questions, handleSubmitAnswer, goToNext, questionIndex, goBack } =
    useContext(QuestionContext)!
  const { uid, isAnonymous } = useContext(AuthContext)!
  const [questionItem, setQuestionItem] = useState<Question>(question)
  const [selected, setSelected] = useState<string | null>(null)
  const [isActive, setIsActive] = useState<boolean>(false)

  useEffect(() => {
    if (questions.length > 1) {
      //Group Questions
      if (questionIndex > questions.length) {
        return
      }
    }
    let q = questions[questionIndex]
    if (q) {
      if (q.startTime && q.startTime > Date.now() / 1000) {
        // if question is not live yet
        return
      }
      setQuestionItem(q)
    }
    checkUserSelected(q)
    setQuestionStatus(q)
  }, [])

  useEffect(() => {
    if (questionIndex > questions.length) {
      //TODO: send to summary page
      return
    }

    let q = questions[questionIndex]
    if (q) {
      setQuestionItem(q)
    }
    checkUserSelected(q)
    setQuestionStatus(q)
  }, [questionIndex, uid])

  useEffect(() => {
    if (questionIndex > questions.length) {
      return
    }

    let q = questions[questionIndex]
    if (q) {
      setQuestionItem(q)
      checkUserSelected(q)
      let answerChanged =
        questionItem && q.userAnswerCode !== questionItem.userAnswerCode
      if (answerChanged) {
        setQuestionItem(q)
      }
    }
  }, [questions])

  const setQuestionStatus = (q: Question) => {
    const questionIsActive = q?.endTime ? isQuestionActive(q.endTime) : true
    setIsActive(questionIsActive)
  }

  const onSelected = async (sel: string) => {
    const prevSel = selected
    setSelected(sel)
    const success = await handleSubmitAnswer(question, sel)
    if (!success) {
      setSelected(prevSel)
      return
    }
    updateAnswerCounts(sel, prevSel)
    if (questions.length > 1) {
      //Auto advance to next question
      setTimeout(() => {
        goToNext(questionIndex)
      }, 1500)
    }
  }

  const onNavControlSelect = async (controlName: 'back' | 'next') => {
    if (controlName === 'back') {
      goBack(questionIndex)
    } else {
      //NEXT
      goToNext(questionIndex)
    }
  }

  const updateAnswerCounts = async (sel: string, prevSel: string | null) => {
    let q = questionItem
    if (!q) return
    let newSelected = q.answerChoices.findIndex((a) => a.code === sel)
    let oldSelected = q.answerChoices.findIndex((a) => a.code === prevSel)
    if (newSelected !== -1) {
      //increment new count by 1
      q.answerChoices[newSelected].count++
    }
    if (oldSelected !== -1) {
      //subtract old count by 1
      q.answerChoices[oldSelected].count--
    }
    q.userAnswerCode = sel

    setQuestionItem(q)
    return
  }

  const checkUserSelected = useCallback(
    (q: Question) => {
      if (!q.userAnswerCode) return null
      const answer = q.answerChoices.find((a) => a.code === q.userAnswerCode)
      if (!answer) return null
      setSelected(answer.code)
      return answer.code
    },
    [uid, questionItem]
  )

  const checkCorrect = useCallback(
    (q: Question, answerRowCode: string) => {
      if (!q.userAnswerCode || !q.correctAnswerCode) return 0
      const rowWithCorrectAnswer = answerRowCode === q.correctAnswerCode
      const userWithCorrectAnswer = q.userAnswerCode === q.correctAnswerCode
      if (rowWithCorrectAnswer && userWithCorrectAnswer) {
        return 2
      }
      if (!rowWithCorrectAnswer && userWithCorrectAnswer) {
        return 1
      }
      return 0
    },
    [uid, questionItem]
  )

  return (
    <QuestionBox uiTheme={uiTheme}>
      {questionItem && (
        <div className="questionRow">
          <div className="titleCon">
            {isActive ? <PollIcon className="icon" /> : <LockIcon className="icon" />}
            <div className="questionTextCon">
              <h4 className="questionText">{questionItem.question}</h4>
              <QuestionStatus question={questionItem} />
            </div>
          </div>
          <Share questionLink={questionItem.shareLink} size="lg" />
        </div>
      )}
      {questionItem?.answerChoices &&
        questionItem?.answerChoices.length > 0 &&
        questionItem.answerChoices.map((answer) => (
          <AnswerRowCon
            key={answer.code}
            uiTheme={uiTheme}
            question={question}
            answer={answer}
            isAnswered={selected ? true : false}
            correct={checkCorrect(questionItem, answer.code)}
            selected={selected}
            onSelect={onSelected}
          />
        ))}
      <ErrorCon questionId={questionItem.questionId} />
      <div className="navControlsCon">
        {questionIndex !== 0 ? (
          <div className="navControl" onClick={() => onNavControlSelect('back')}>
            Back
          </div>
        ) : (
          <div />
        )}
        <div className="navControl" onClick={() => onNavControlSelect('next')}>
          {questions.length === 1
            ? ''
            : questionIndex < questions.length - 1
            ? 'Next'
            : 'Summary'}
        </div>
      </div>
      {questionItem.appExclusive && (
        <AppExclusiveOverlay>
          <AppExclusive questionLink={questionItem.shareLink || ''} />
        </AppExclusiveOverlay>
      )}
      {!questionItem.appExclusive && isAnonymous && (
        <LoginQuestionCallout question={questionItem} />
      )}
    </QuestionBox>
  )
}
